/**
 * Payment Types for Stripe Integration
 * 
 * TypeScript interfaces and enums for the payment system.
 */

// ============= ENUMS =============

export enum BillingInterval {
  ONE_TIME = 'one_time',
  MONTHLY = 'monthly',
  QUARTERLY = 'quarterly',
  YEARLY = 'yearly',
}

export enum PaymentStatus {
  CREATED = 'created',
  REQUIRES_ACTION = 'requires_action',
  PROCESSING = 'processing',
  SUCCEEDED = 'succeeded',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
  REFUNDED = 'refunded',
  PARTIALLY_REFUNDED = 'partially_refunded',
}

export enum SubscriptionStatus {
  INCOMPLETE = 'incomplete',
  INCOMPLETE_EXPIRED = 'incomplete_expired',
  TRIALING = 'trialing',
  ACTIVE = 'active',
  PAST_DUE = 'past_due',
  CANCELED = 'canceled',
  UNPAID = 'unpaid',
  PAUSED = 'paused',
}

export enum ProductType {
  SUBSCRIPTION = 'subscription',
  ONE_TIME = 'one_time',
}

// ============= PRODUCT TYPES =============

export interface Product {
  id: string;
  name: string;
  description?: string;
  product_type: ProductType;
  price_amount: number;
  currency: string;
  billing_interval: BillingInterval;
  features?: string[];
  is_active: boolean;
  display_order: number;
  stripe_product_id?: string;
  stripe_price_id?: string;
  created_at: string;
  updated_at: string;
  formatted_price?: string;
}

export interface ProductListResponse {
  products: Product[];
  total: number;
}

// ============= PAYMENT TYPES =============

export interface PaymentIntentCreate {
  product_id: string;
  quantity?: number;
  coupon_code?: string;
}

export interface PaymentIntentResponse {
  payment_id: string;
  client_secret: string;
  amount: number;
  currency: string;
  status: PaymentStatus;
  publishable_key: string;
}

export interface Payment {
  id: string;
  user_id: string;
  product_id?: string;
  product_name?: string;
  amount: number;
  currency: string;
  quantity: number;
  status: PaymentStatus;
  stripe_payment_intent_id?: string;
  failure_code?: string;
  failure_message?: string;
  refunded_amount?: number;
  created_at: string;
  updated_at: string;
  completed_at?: string;
}

export interface PaymentHistoryResponse {
  payments: Payment[];
  total: number;
}

// ============= SUBSCRIPTION TYPES =============

export interface SubscriptionCreate {
  product_id: string;
  coupon_code?: string;
  trial_days?: number;
}

export interface SubscriptionCreateResponse {
  subscription_id: string;
  stripe_subscription_id?: string;
  status: SubscriptionStatus;
  client_secret?: string;
  publishable_key: string;
  requires_action: boolean;
}

export interface Subscription {
  id: string;
  user_id: string;
  product_id?: string;
  product_name?: string;
  stripe_subscription_id?: string;
  status: SubscriptionStatus;
  current_period_start?: string;
  current_period_end?: string;
  trial_start?: string;
  trial_end?: string;
  cancel_at_period_end: boolean;
  cancel_at?: string;
  canceled_at?: string;
  created_at: string;
  updated_at: string;
}

export interface SubscriptionListResponse {
  subscriptions: Subscription[];
  total: number;
}

export interface SubscriptionCancelRequest {
  cancel_immediately: boolean;
}

// ============= SETUP INTENT =============

export interface SetupIntentResponse {
  client_secret: string;
  publishable_key: string;
}

// ============= CUSTOMER PORTAL =============

export interface CustomerPortalResponse {
  portal_url: string;
}

// ============= ENTITLEMENTS =============

export interface UserEntitlements {
  user_id: string;
  has_active_subscription: boolean;
  subscription_plan?: string;
  subscription_status?: SubscriptionStatus;
  subscription_ends_at?: string;
  credits_remaining: number;
  wallet_balance: number;
  wallet_currency: string;
  pending_claims_count: number;
  active_subject_grants: Array<{
    subject_id: string;
    subject_name: string;
    expires_at: string;
    registered_at?: string;
  }>;
  pricing_snapshot: Record<string, any>;
  features: string[];
}

export enum EcoCashReceiptSource {
  SMS = 'sms',
  NOTIFICATION = 'notification',
  API = 'api',
}

export enum EcoCashClaimStatus {
  PENDING_RECEIPT = 'pending_receipt',
  MATCHED_PENDING_ALLOCATION = 'matched_pending_allocation',
  APPROVED = 'approved',
  INSUFFICIENT_AMOUNT = 'insufficient_amount',
  DUPLICATE_TRANSACTION = 'duplicate_transaction',
  ALREADY_USED = 'already_used',
  MANUAL_REVIEW = 'manual_review',
  REJECTED = 'rejected',
}

export enum SubjectPaymentMethod {
  ECOCASH = 'ecocash',
  STRIPE = 'stripe',
  PAYNOW = 'paynow',
}

export enum SubjectPaymentCheckoutStatus {
  AWAITING_PAYMENT = 'awaiting_payment',
  AWAITING_RECEIPT = 'awaiting_receipt',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

export interface EcoCashQuoteItem {
  subject_id: string;
  months: number;
}

export interface EcoCashQuoteRequest {
  items: EcoCashQuoteItem[];
  use_wallet_balance?: boolean;
}

export interface EcoCashPricingSnapshot {
  subject_price_cents: number;
  pricing_mode?: string;
  standard_subject_price_cents?: number;
  premium_subject_price_cents?: number;
  currency: string;
  merchant_code: string;
  merchant_name: string;
  instructions: string;
}

export interface EcoCashQuoteResponse {
  checkout_reference: string;
  currency: string;
  required_amount: number;
  wallet_balance: number;
  wallet_applied: number;
  amount_due: number;
  items: Array<{
    subject_id: string;
    subject_name: string;
    months: number;
    amount: number;
    monthly_price_cents?: number;
  }>;
  pricing_snapshot: EcoCashPricingSnapshot;
}

export interface SubjectPaymentCheckoutResponse {
  id: string;
  user_id: string;
  checkout_reference: string;
  payment_method: SubjectPaymentMethod;
  status: SubjectPaymentCheckoutStatus;
  requested_amount: number;
  external_amount_due: number;
  wallet_applied: number;
  currency: string;
  requested_items: EcoCashQuoteItem[];
  reason_code?: string;
  feedback_reason?: string;
  payment_id?: string;
  ecocash_claim_id?: string;
  provider_reference?: string;
  redirect_url?: string;
  poll_url?: string;
  wallet_balance: number;
  active_subject_grants: Array<{
    subject_id: string;
    subject_name: string;
    expires_at: string;
    registered_at?: string;
  }>;
  created_at: string;
  updated_at: string;
  completed_at?: string;
}

export interface SubjectAccessStripeIntentRequest {
  items: EcoCashQuoteItem[];
  use_wallet_balance?: boolean;
}

export interface SubjectAccessStripeIntentResponse {
  checkout: SubjectPaymentCheckoutResponse;
  payment_intent: PaymentIntentResponse | null;
}

export interface SubjectAccessPaynowInitiateRequest {
  items: EcoCashQuoteItem[];
  use_wallet_balance?: boolean;
}

export interface SubjectAccessPaynowInitiateResponse {
  checkout: SubjectPaymentCheckoutResponse;
}

export interface PaymentCapabilitiesResponse {
  subject_access_methods: SubjectPaymentMethod[];
  subscriptions_public_enabled: boolean;
  customer_portal_public_enabled: boolean;
}

export interface EcoCashReceiptResponse {
  id: string;
  ingest_key: string;
  source: EcoCashReceiptSource;
  sender_address?: string;
  source_package?: string;
  merchant_device_id?: string;
  merchant_app_version?: string;
  transaction_id?: string;
  payer_name?: string;
  payer_msisdn?: string;
  amount?: number;
  currency?: string;
  parse_status: 'parsed' | 'unparsed' | 'manual_review';
  duplicate_status: 'unique' | 'duplicate';
  consumed_by_claim_id?: string;
  created_at: string;
}

export interface EcoCashClaimCreate {
  transaction_id?: string;
  checkout_reference?: string;
  items: EcoCashQuoteItem[];
  use_wallet_balance?: boolean;
}

export interface EcoCashClaimResponse {
  id: string;
  user_id: string;
  transaction_id?: string;
  checkout_reference?: string;
  requested_amount: number;
  currency: string;
  requested_items: Array<Record<string, any>>;
  claim_status: EcoCashClaimStatus;
  reason_code?: string;
  feedback_reason?: string;
  receipt_id?: string;
  wallet_balance: number;
  active_subject_grants: Array<{
    subject_id: string;
    subject_name: string;
    expires_at: string;
    registered_at?: string;
  }>;
  created_at: string;
  updated_at: string;
}

export interface WalletLedgerEntry {
  id: string;
  entry_type: string;
  amount: number;
  currency: string;
  description?: string;
  claim_id?: string;
  receipt_id?: string;
  created_at: string;
}

export interface WalletSnapshot {
  user_id: string;
  wallet_balance: number;
  wallet_currency: string;
  pending_claims_count: number;
  active_subject_grants: Array<{
    subject_id: string;
    subject_name: string;
    expires_at: string;
    registered_at?: string;
  }>;
  pricing_snapshot: EcoCashPricingSnapshot;
  recent_ledger_entries: WalletLedgerEntry[];
}

// ============= HELPER FUNCTIONS =============

export function formatPrice(amount: number, currency: string = 'usd'): string {
  const amountDecimal = amount / 100;
  if (currency.toLowerCase() === 'usd') {
    return `$${amountDecimal.toFixed(2)}`;
  }
  return `${amountDecimal.toFixed(2)} ${currency.toUpperCase()}`;
}

export function formatBillingInterval(interval: BillingInterval): string {
  const labels: Record<BillingInterval, string> = {
    [BillingInterval.ONE_TIME]: 'One-time',
    [BillingInterval.MONTHLY]: 'per month',
    [BillingInterval.QUARTERLY]: 'per quarter',
    [BillingInterval.YEARLY]: 'per year',
  };
  return labels[interval];
}

export function getPaymentStatusColor(status: PaymentStatus): string {
  const colors: Record<PaymentStatus, string> = {
    [PaymentStatus.CREATED]: 'text-slate-500',
    [PaymentStatus.REQUIRES_ACTION]: 'text-amber-500',
    [PaymentStatus.PROCESSING]: 'text-blue-500',
    [PaymentStatus.SUCCEEDED]: 'text-emerald-500',
    [PaymentStatus.FAILED]: 'text-red-500',
    [PaymentStatus.CANCELLED]: 'text-slate-500',
    [PaymentStatus.REFUNDED]: 'text-purple-500',
    [PaymentStatus.PARTIALLY_REFUNDED]: 'text-purple-400',
  };
  return colors[status];
}

export function getSubscriptionStatusColor(status: SubscriptionStatus): string {
  const colors: Record<SubscriptionStatus, string> = {
    [SubscriptionStatus.INCOMPLETE]: 'text-amber-500',
    [SubscriptionStatus.INCOMPLETE_EXPIRED]: 'text-red-500',
    [SubscriptionStatus.TRIALING]: 'text-blue-500',
    [SubscriptionStatus.ACTIVE]: 'text-emerald-500',
    [SubscriptionStatus.PAST_DUE]: 'text-red-500',
    [SubscriptionStatus.CANCELED]: 'text-slate-500',
    [SubscriptionStatus.UNPAID]: 'text-red-500',
    [SubscriptionStatus.PAUSED]: 'text-amber-500',
  };
  return colors[status];
}

export function getSubscriptionStatusLabel(status: SubscriptionStatus): string {
  const labels: Record<SubscriptionStatus, string> = {
    [SubscriptionStatus.INCOMPLETE]: 'Incomplete',
    [SubscriptionStatus.INCOMPLETE_EXPIRED]: 'Expired',
    [SubscriptionStatus.TRIALING]: 'Trial',
    [SubscriptionStatus.ACTIVE]: 'Active',
    [SubscriptionStatus.PAST_DUE]: 'Past Due',
    [SubscriptionStatus.CANCELED]: 'Canceled',
    [SubscriptionStatus.UNPAID]: 'Unpaid',
    [SubscriptionStatus.PAUSED]: 'Paused',
  };
  return labels[status];
}

export function getEcoCashClaimStatusLabel(status: EcoCashClaimStatus): string {
  const labels: Record<EcoCashClaimStatus, string> = {
    [EcoCashClaimStatus.PENDING_RECEIPT]: 'Waiting for receipt',
    [EcoCashClaimStatus.MATCHED_PENDING_ALLOCATION]: 'Matching payment',
    [EcoCashClaimStatus.APPROVED]: 'Approved',
    [EcoCashClaimStatus.INSUFFICIENT_AMOUNT]: 'Insufficient amount',
    [EcoCashClaimStatus.DUPLICATE_TRANSACTION]: 'Duplicate transaction',
    [EcoCashClaimStatus.ALREADY_USED]: 'Already used',
    [EcoCashClaimStatus.MANUAL_REVIEW]: 'Manual review',
    [EcoCashClaimStatus.REJECTED]: 'Rejected',
  };
  return labels[status];
}
